import apiSkobo from "@/helpers/api/apiSkobo";
import TrackHelper from "@/helpers/utilities/TrackHelper";
import { useFeaturesStore } from "@/stores/features";
import { useDocumentStore } from "@/stores/documents";
import { useMeStore } from "@/stores/me";
import { SessionHelper } from "@/helpers/utilities/sessionHelper";

import vuexStore from "@/helpers/store";
import {
  setupApplicationFavicon,
  setupLocalization,
  setupRolesInVueContext,
  setupUserContext,
  setupVCalendar
} from "@/services/setupService";

export const applicationSetup_state = {
  applicationSetup: {},

  templateFoldersState: null,
  templateStructureState: null,
  templatespecialItemsState: null
};

export const applicationSetup_actions = {
  async loadSetup(setupObject) {
    try {
      if (!setupObject) {
        const result = await apiSkobo.get("/me/setup");
        setupObject = result.data;
      }

      setupRolesInVueContext(setupObject);
      setupUserContext(setupObject);
      setupApplicationTitle(setupObject);
      setupApplicationFavicon(setupObject);
      await setupLocalization(setupObject);
      setupVCalendar(setupObject);

      const applicationSetup = parseSetup(setupObject);
      this.applicationSetup = applicationSetup;

      // Template setup
      const templateFoldersJson = setupObject.settings.find(
        s => s.typename === "Templates" && s.key === "folders"
      )?.value;
      this.templateFoldersState = templateFoldersJson ? JSON.parse(templateFoldersJson) : null;

      const templateStructureJson = setupObject.settings.find(
        s => s.typename === "Templates" && s.key === "structure"
      )?.value;
      this.templateStructureState = templateStructureJson ? JSON.parse(templateStructureJson) : null;

      const templateSpecialItemsJson = setupObject.settings.find(
        s => s.typename === "Templates" && s.key === "specialitems"
      )?.value;
      this.templatespecialItemsState = templateSpecialItemsJson ? JSON.parse(templateSpecialItemsJson) : null;

      vuexStore.commit("setup/setupStoreVars", applicationSetup);

      const featureStore = useFeaturesStore();
      featureStore.addFeatures(setupObject.features);

      const documentStore = useDocumentStore();
      documentStore.setupSensitivityLabels(setupObject.sensitivityLabels);

      const meStore = useMeStore();
      meStore.loadMe(setupObject.userProfile);
      this.setTheme(setupObject.layout);
    } catch (e) {
      TrackHelper.registerError(e);
      throw e;
    }
  }
};

// Setup vars in store

const parseSetup = setup => {
  const applicationSetup = {};

  applicationSetup.interfaces = {
    tenant: setup.interfaces.tenant,
    graphAPIClientId: setup.interfaces.msGraphAPI.graphAPIClientId
  };

  applicationSetup.browserLocale = setup.localization.find(k => k.key === "browserlocale")?.value;
  applicationSetup.language = setup.localization.find(k => k.key === "language")?.value;
  applicationSetup.localeFormats = {
    browserLocale: !setup || !applicationSetup.browserLocale ? "da-DK" : applicationSetup.browserLocale.value,
    shortDateFormatOptions: {
      year: "numeric",
      month: "numeric",
      day: "numeric"
    },
    longDateFormOptions: {
      year: "numeric",
      month: "long",
      day: "numeric"
    },
    shortDateFormOptions: {
      year: "numeric",
      month: "short",
      day: "numeric"
    },
    veryLongDateFormOptions: {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    },
    timeOptions: {
      hour: "numeric",
      minute: "numeric"
    }
  };

  // Sharing listtypes setup
  const sharingListTypesSetup = setup.settings.find(s => s.key === "sharinglisttypesetup");

  applicationSetup.sharingListTypes = sharingListTypesSetup
    ? JSON.parse(sharingListTypesSetup.value)
    : { student: [], teacher: [] };

  // CopyTo and CopyFrom setups
  const copyDocumentsToOneDrive = setup.settings.find(s => s.key === "copydocumentstoonedrive");
  if (copyDocumentsToOneDrive) {
    applicationSetup.copyDocumentsToOneDrive = copyDocumentsToOneDrive.value === "true";
  }

  const copyDocumentsFromOnedrive = setup.settings.find(s => s.key === "copydocumentsfromdrive");
  if (copyDocumentsFromOnedrive) {
    applicationSetup.copyDocumentsFromOnedrive = copyDocumentsFromOnedrive.value === "true";
  }

  const copyDocumentsToSectionsStudent = setup.settings.find(s => s.key === "copydocumentstosectionsstudent");
  if (copyDocumentsToSectionsStudent) {
    applicationSetup.copyDocumentsToSectionsStudent = copyDocumentsToSectionsStudent.value === "true";
  }

  const copyDocumentsFromSectionsStudent = setup.settings.find(s => s.key === "copydocumentsfromsectionsstudent");
  if (copyDocumentsFromSectionsStudent) {
    applicationSetup.copyDocumentsFromSectionsStudent = copyDocumentsFromSectionsStudent.value === "true";
  }

  const copyDocumentsToSectionsTeacher = setup.settings.find(s => s.key === "copydocumentstosectionsteacher");
  if (copyDocumentsToSectionsTeacher) {
    applicationSetup.copyDocumentsToSectionsTeacher = copyDocumentsToSectionsTeacher.value === "true";
  }

  const copyDocumentsFromSectionsTeacher = setup.settings.find(s => s.key === "copydocumentsfromsectionsteacher");
  if (copyDocumentsFromSectionsTeacher) {
    applicationSetup.copyDocumentsFromSectionsTeacher = copyDocumentsFromSectionsTeacher.value === "true";
  }

  const showArchivedSectionsSetup = setup.settings.find(s => s.key === "showarchivedsections");
  if (showArchivedSectionsSetup) {
    applicationSetup.showArchivedSections = showArchivedSectionsSetup.value === "true";
  }

  const logEndpointsSetup = setup.settings.find(s => s.key === "logendpoints");
  if (logEndpointsSetup) {
    applicationSetup.logEndpoints = JSON.parse(logEndpointsSetup.value);
  }

  const os2faktorWaitForAuthenticationTimeout = setup.settings.find(
    s => s.key === "os2faktorwaitforauthenticationtimeout"
  );
  if (os2faktorWaitForAuthenticationTimeout) {
    applicationSetup.os2faktorWaitForAuthenticationTimeout = parseInt(os2faktorWaitForAuthenticationTimeout.value);
  }

  const os2faktorSessionTimeout = setup.settings.find(s => s.key === "os2faktorsessiontimeout");
  if (os2faktorSessionTimeout) {
    applicationSetup.os2faktorSessionTimeout = parseInt(os2faktorSessionTimeout.value);
  }

  const sectionConfiguration = setup.settings.find(s => s.key === "sectionconfiguration");
  if (sectionConfiguration) {
    applicationSetup.sectionConfiguration = JSON.parse(sectionConfiguration.value);
  }

  const fileConfiguration = setup.settings.find(s => s.key === "fileconfiguration");
  if (fileConfiguration) {
    applicationSetup.fileConfiguration = JSON.parse(fileConfiguration.value);
  }

  const navigationConfiguration = setup.settings.find(s => s.key === "navigationconfiguration");
  if (navigationConfiguration) {
    applicationSetup.navigationConfiguration = JSON.parse(navigationConfiguration.value);
  }

  const shareToAulaDefaultDaysConfiguration = setup.settings.find(s => s.key === "sharetoauladefaultdays");
  if (shareToAulaDefaultDaysConfiguration) {
    applicationSetup.shareToAulaDefaultDaysConfiguration = JSON.parse(shareToAulaDefaultDaysConfiguration.value);
  }

  const confidentialNoteTypes = setup.settings.find(s => s.key === "confidentialnotetypes");
  if (confidentialNoteTypes) {
    applicationSetup.confidentialNoteTypes = JSON.parse(confidentialNoteTypes.value);
  }

  // CleanUpDrives
  if (setup.drives) {
    applicationSetup.drives = setup.drives
      .filter(d => d.additionalData && d.additionalData.drives && d.additionalData.drives.length > 0)
      .map(d => ({
        group: d.group,
        name: d.name,
        driveId: d.additionalData.drives[0].id,
        itemId: d.additionalData.drives[0].itemId
      }));
  }

  return applicationSetup;
};

export const applicationSetup_getters = {
  templateDriveId: state => {
    const templateDriveSetup = state.applicationSetup?.drives?.find(
      d => d.group === "Provided.O365.Teams.DocumentTemplate"
    );
    if (templateDriveSetup) {
      return templateDriveSetup.driveId;
    }
    return null;
  },
  templateFolders: state => state.templateFoldersState,
  templateStructure: state => state.templateStructureState,
  templateSpecialItems: state => state.templatespecialItemsState || [],

  sectionConfiguration: state => state.applicationSetup.sectionConfiguration,
  navigationConfiguration: state => state.applicationSetup.navigationConfiguration,
  confidentialNoteTypes: state => state.applicationSetup.confidentialNoteTypes || [],
  fileConfiguration: state => state.applicationSetup.fileConfiguration || {},
  interfaces: state => state.applicationSetup.interfaces,
  shareToAulaDefaultDays: state => state.applicationSetup.shareToAulaDefaultDaysConfiguration,

  language: state => state.applicationSetup.language,
  browserLocale: state => state.applicationSetup.browserLocale,
  localeFormats: state => state.applicationSetup.localeFormats || {},
  logEndpoints: state => state.applicationSetup.logEndpoints || [],

  copyDocumentsFromSectionsStudent: state => state.applicationSetup.copyDocumentsFromSectionsStudent,
  copyDocumentsToSectionsStudent: state => state.applicationSetup.copyDocumentsToSectionsStudent,
  copyDocumentsFromSectionsTeacher: state => state.applicationSetup.copyDocumentsFromSectionsTeacher,
  copyDocumentsToSectionsTeacher: state => state.applicationSetup.copyDocumentsToSectionsTeacher,

  copyFromShouldIncludeSections: state => {
    const meStore = useMeStore();
    return (
      (state.applicationSetup.copyDocumentsFromSectionsTeacher && meStore.isTeacher) ||
      (state.applicationSetup.copyDocumentsFromSectionsStudent && meStore.isStudent)
    );
  },
  copyDocumentsToOneDrive: state => state.applicationSetup.copyDocumentsToOneDrive,
  copyDocumentsFromOnedrive: state => state.applicationSetup.copyDocumentsFromOnedrive,

  setting: state => settingName => state.applicationSetup[settingName]
};

/**
 * Sets the title bar of the browser
 * @param { object } setup Setup object from api
 */
function setupApplicationTitle(setup) {
  const titleSetup = setup.settings.find(k => k.key === "browsertitle");
  if (titleSetup) {
    document.title = import.meta.env.VITE_APP_ENVIRONMENT_TITLE
      ? "(" + import.meta.env.VITE_APP_ENVIRONMENT_TITLE + ") "
      : "" + titleSetup.value;

    SessionHelper.addCustomerSystemName(titleSetup.value);
  }

  if (import.meta.env.VITE_APP_ENVIRONMENT_TITLE) {
    document.title = "(" + import.meta.env.VITE_APP_ENVIRONMENT_TITLE + ") " + document.title;
  }
}
